import React, {useCallback, useRef, useState} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import {useDispatch} from "react-redux";
import {getGuests} from "redux/actions/guestsActions";
import {buildStyles} from "connectui/component/buildStyles";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import Skeleton from "connectui/component/display/Skeleton";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import Container from "connectui/component/layout/Container";
import TextInput from "connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "connectui/component/input/text/OutlinedInputBase";
import FlexContainer from "connectui/component/layout/FlexContainer";
import VirtualizedListComponent from "connectui/component/layout/list/VirtualizedListComponent";
import Text from "connectui/component/display/Text";
import CheckBox from "connectui/component/input/CheckBox";
import ListComponent from "../../../../connectui/component/layout/list/ListComponent";
import ButtonText from "../../../../connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "../../../../connectui/component/input/button/base/OutlinedButtonBase";
import {useTranslation} from "react-i18next";
import {getEncodedFilters, sleep} from "utilities/helperFunctions";

const useStyles = (theme) =>
  buildStyles({
    contextMenuContainer: {
      padding: theme.spacing(2, 2),
      minWidth: "300px",
      maxHeight: "400px",
      display: "flex",
      flexDirection: "column",
    },
    searchIcon: {
      width: "15px",
      height: "15px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    xIcon: {
      width: "11px",
      height: "11px",
      cursor: "pointer",
      color: theme.colors.gray500,
    },
    listContainer: {
      overflow: "auto",
      marginTop: theme.spacing(2),
    },
    listItem: {
      padding: theme.spacing(2.5, 3.5),
      cursor: "pointer",
      height: "100%",
      "&:hover": {
        backgroundColor: theme.colors.gray50,
      },
    },
  });

// Define guest type icons?
const guestTypeIcons = {
  guest: ICONS.user,
  owner: ICONS["home-owner"],
  maintenance: ICONS.tool,
  cleaner: ICONS.clean,
};

function GuestSelectorMenu({
  open,
  onClose,
  anchorEl,
  selectedIds,
  onGuestSelection,
  modalProps = {},
  ensoKey,
  onGuestsLoaded,
  guestFilters = [
    {
      path: "guest_type",
      value: ["owner", "maintenance", "cleaner"],
    },
    {path: "guest_tags", operator: "!=", value: "archived"},
  ],
}) {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [guestList, setGuestList] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [pageStart, setPageStart] = useState(0);
  const currentSearch = useRef("");
  const isMounted = useRef(false);

  const filteredGuestList = guestList.filter(
    (guest) => guest.name && guest.guest_type,
  );

  const isRowLoaded = useCallback(
    ({index}) => {
      return index < filteredGuestList.length;
    },
    [filteredGuestList],
  );

  const loadPage = useCallback(
    (start, query) => {
      if (start === 0) setLoadingGuests(true);
      let params = `enso_key=${ensoKey || ""}&start=${start}`;
      if (query) params += `&query=${encodeURIComponent(query)}`;
      params += `&filters=${getEncodedFilters(guestFilters)}`;

      dispatch(
        getGuests({
          params,
          skipLocalUpdate: true,
          add: start > 0,
          onSuccess: (response) => {
            let totalGuests = 0;
            if (start === 0) {
              totalGuests = response.hits.length;
              setGuestList(response.hits);
            } else {
              let newGuests = guestList.concat(response.hits);
              totalGuests = newGuests.length;
              setGuestList(newGuests);
            }
            setHasNextPage(!!response.start_key);
            setPageStart(totalGuests);
            setLoadingGuests(false);

            if (onGuestsLoaded) {
              onGuestsLoaded(response.hits);
            }
          },
          onError: () => {
            setHasNextPage(false);
            setLoadingGuests(false);
          },
        }),
      );
    },
    [dispatch, ensoKey, guestFilters, guestList, onGuestsLoaded],
  );

  const handleSearchChange = (value) => {
    currentSearch.current = value;
    setSearchText(value);

    if (!isMounted.current) return;
    setGuestList([]);
    setLoadingGuests(true);
    sleep(500).then(() => {
      if (value === currentSearch.current) {
        loadPage(0, value);
      }
    });
  };

  const handleSearchClear = () => {
    currentSearch.current = "";
    setSearchText("");
    loadPage(0, "");
  };

  const loadMoreRows = useCallback(() => {
    if (!hasNextPage || loadingGuests || !anchorEl) return;
    loadPage(pageStart, searchText);
  }, [hasNextPage, loadingGuests, anchorEl, loadPage, pageStart, searchText]);

  React.useEffect(() => {
    if (anchorEl && !isMounted.current) {
      isMounted.current = true;
      loadPage(0, searchText);
    }
  }, [anchorEl, loadPage, searchText]);

  const renderVirtualizedList = () => (
    <VirtualizedListComponent
      totalRows={
        hasNextPage ? filteredGuestList.length + 1 : filteredGuestList.length
      }
      rowHeight={44}
      isRowLoaded={isRowLoaded}
      loadMoreRows={loadMoreRows}
    >
      {(index) => {
        const guestItem = filteredGuestList[index];
        if (!guestItem)
          return (
            <Skeleton
              width={"100%"}
              height={"44px"}
              style={{borderRadius: `${theme.radius.lg}px`}}
            />
          );
        const isSelected = selectedIds.includes(guestItem.guest_id);
        return (
          <FlexContainer
            key={guestItem.guest_id}
            alignItems="center"
            justifyContent="space-between"
            className={classes.listItem}
            onClick={() =>
              onGuestSelection(guestItem.guest_id, isSelected, guestItem)
            }
          >
            <FlexContainer alignItems="center" gap={theme.spacing(2)}>
              <SvgIcon
                component={guestTypeIcons[guestItem.guest_type]}
                sx={{
                  width: "16px",
                  height: "16px",
                  color: theme.colors.textSecondary,
                }}
              />
              <Text variant="textSM">{guestItem.name}</Text>
            </FlexContainer>
            <CheckBox isChecked={isSelected} />
          </FlexContainer>
        );
      }}
    </VirtualizedListComponent>
  );

  return (
    <ContextMenu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      modalProps={modalProps}
      customContent={
        <Container className={classes.contextMenuContainer}>
          <TextInput
            inputBase={OutlinedInputBase}
            startIcon={
              <SvgIcon
                component={ICONS.search}
                className={classes.searchIcon}
                sx={{color: theme.colors.gray500}}
              />
            }
            inputProps={{
              placeholder: "Search guests",
              value: searchText,
              autoFocus: true,
              onChange: (e) => {
                handleSearchChange(e.target.value);
                e.stopPropagation();
              },
            }}
            actions={
              !!searchText && (
                <SvgIcon
                  component={ICONS.x}
                  className={classes.xIcon}
                  onClick={handleSearchClear}
                />
              )
            }
          />
          <FlexContainer
            flexDirection="column"
            className={classes.listContainer}
            sx={{
              height: "400px",
            }}
          >
            {loadingGuests ? (
              <ListComponent
                gap={theme.spacing(1)}
                sx={{width: "100%", height: "100%"}}
              >
                {Array.from({length: 7}).map((_, index) => (
                  <Skeleton
                    key={index}
                    width={"100%"}
                    height={"44px"}
                    style={{borderRadius: `${theme.radius.lg}px`}}
                  />
                ))}
              </ListComponent>
            ) : filteredGuestList.length > 0 ? (
              renderVirtualizedList()
            ) : (
              <FlexContainer
                alignItems={"center"}
                justifyContent={"center"}
                flexDirection={"column"}
                gap={theme.spacing(2)}
                sx={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <Text color={"textSecondary"} variant={"textMD"}>
                  {t(`inbox.guest-details.followers.no-results`)}
                </Text>
                <ButtonText
                  buttonBase={OutlinedButtonBase}
                  label={t(`inbox.guest-details.followers.clear-query`)}
                  onClick={handleSearchClear}
                ></ButtonText>
              </FlexContainer>
            )}
          </FlexContainer>
        </Container>
      }
    />
  );
}

export default GuestSelectorMenu;
